/* eslint-disable camelcase */
import { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Label, Input, Spinner, Table,
} from 'reactstrap';
import { Plus, Edit3, Trash2, Save, X, AlertTriangle } from 'react-feather';
import ModalClickable from '../../layouts/modals/ModalClickable';
import descuentosApiService from '../../services/http/descuentosApiService';
import logger from '../../services/logger';
import colors from '../../theme/colors';

const HEADQUARTERS = ['CEDESCA', 'CNTEC'];

const EMPTY_FORM = { id: '', descuento: '', porcentaje: '', fecha_inicio: '', fecha_fin: '' };

const formatFecha = (f) => {
  if (!f) return '';
  const d = new Date(f);
  if (Number.isNaN(d.getTime())) return f;
  return d.toLocaleDateString('es-ES');
};

const toInputDate = (f) => {
  if (!f) return '';
  if (typeof f === 'string' && /^\d{4}-\d{2}-\d{2}/.test(f)) return f.slice(0, 10);
  const d = new Date(f);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

const Descuentos = () => {
  const [headquarter, setHeadquarter] = useState(HEADQUARTERS[0]);
  const [descuentos, setDescuentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');

  const cargar = useCallback(async (hq) => {
    setLoading(true);
    try {
      const data = await descuentosApiService.listarDescuentos({ headquarter: hq });
      setDescuentos(Array.isArray(data) ? data : []);
    } catch (err) {
      logger.error('Error cargando descuentos:', err);
      setDescuentos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar(headquarter);
  }, [cargar, headquarter]);

  const abrirNuevo = () => {
    setForm(EMPTY_FORM);
    setEditing(false);
    setError('');
    setModalOpen(true);
  };

  const abrirEditar = (d) => {
    setForm({
      id: d.id,
      descuento: d.descuento ?? '',
      porcentaje: d.porcentaje ?? '',
      fecha_inicio: toInputDate(d.fecha_inicio),
      fecha_fin: toInputDate(d.fecha_fin),
    });
    setEditing(true);
    setError('');
    setModalOpen(true);
  };

  const cerrarModal = () => {
    if (saving) return;
    setModalOpen(false);
    setForm(EMPTY_FORM);
    setError('');
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const extraerErrorBackend = (err) => {
    const data = err?.response?.data;
    if (!data) return '';
    if (data.message) return data.message;
    if (Array.isArray(data.details) && data.details.length > 0) {
      return data.details.map((d) => d.message || JSON.stringify(d)).join(', ');
    }
    return '';
  };

  const handleGuardar = async () => {
    setError('');
    if (!form.descuento.trim()) {
      setError('La descripción del descuento es obligatoria.');
      return;
    }
    const pct = Number(form.porcentaje);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      setError('El porcentaje debe ser un número entre 0 y 100.');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await descuentosApiService.actualizarDescuento({
          headquarter,
          id: form.id,
          descuento: form.descuento.trim(),
          porcentaje: pct,
          fecha_inicio: form.fecha_inicio || undefined,
          fecha_fin: form.fecha_fin || undefined,
        });
        setFeedback('Descuento actualizado correctamente.');
      } else {
        await descuentosApiService.crearDescuento({
          headquarter,
          descuento: form.descuento.trim(),
          porcentaje: pct,
          fecha_inicio: form.fecha_inicio || undefined,
          fecha_fin: form.fecha_fin || undefined,
        });
        setFeedback('Descuento creado correctamente.');
      }
      setModalOpen(false);
      setForm(EMPTY_FORM);
      await cargar(headquarter);
    } catch (err) {
      logger.error('Error guardando descuento:', err);
      setError(extraerErrorBackend(err) || 'Error al guardar el descuento.');
    } finally {
      setSaving(false);
    }
  };

  const pedirConfirmarBorrar = (descuento) => {
    setConfirmDelete(descuento);
  };

  const cancelarBorrar = () => {
    if (deletingId) return;
    setConfirmDelete(null);
  };

  const confirmarBorrar = async () => {
    if (!confirmDelete) return;
    const { id } = confirmDelete;
    setDeletingId(id);
    try {
      await descuentosApiService.eliminarDescuento({ headquarter, id });
      setFeedback('Descuento eliminado correctamente.');
      setConfirmDelete(null);
      await cargar(headquarter);
    } catch (err) {
      logger.error('Error eliminando descuento:', err);
      setFeedback('Error al eliminar el descuento.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      {feedback && <ModalClickable color="primary" header={feedback} />}
      <div
        style={{
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '16px',
        }}
      >
        <div>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', margin: 0 }}>
            Descuentos
          </h1>
          <p style={{ color: '#9ca3af', marginTop: '2px', marginBottom: 0, fontSize: '0.8rem' }}>
            Gestión de descuentos aplicables (admin_descuentos)
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
          <div>
            <Label style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '2px' }}>
              Centro
            </Label>
            <Input
              type="select"
              value={headquarter}
              onChange={(e) => setHeadquarter(e.target.value)}
              disabled={loading || saving}
              style={{ minWidth: '140px' }}
            >
              {HEADQUARTERS.map((h) => (
                <option key={h} value={h}>{h}</option>
              ))}
            </Input>
          </div>
          <Button
            color="primary"
            onClick={abrirNuevo}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              backgroundColor: colors.primary,
              border: 'none',
            }}
          >
            <Plus size={14} /> Nuevo descuento
          </Button>
        </div>
      </div>

      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgb(0 0 0 / 0.06)',
          padding: '12px 16px',
        }}
      >
        {loading ? (
          <div className="text-center" style={{ padding: '24px' }}>
            <Spinner color="primary" /> Cargando descuentos...
          </div>
        ) : descuentos.length === 0 ? (
          <p
            className="text-muted text-center"
            style={{ fontSize: '0.85rem', margin: 0, padding: '16px' }}
          >
            No hay descuentos registrados en {headquarter}.
          </p>
        ) : (
          <Table responsive borderless style={{ fontSize: '0.85rem', margin: 0 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                <th style={{ width: '60px' }}>ID</th>
                <th>Descuento</th>
                <th style={{ width: '120px' }}>Porcentaje</th>
                <th style={{ width: '140px' }}>Fecha inicio</th>
                <th style={{ width: '140px' }}>Fecha fin</th>
                <th style={{ width: '120px', textAlign: 'right' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {descuentos.map((d) => (
                <tr key={d.id}>
                  <td>{d.id}</td>
                  <td>{d.descuento}</td>
                  <td>{d.porcentaje}%</td>
                  <td>{formatFecha(d.fecha_inicio)}</td>
                  <td>{formatFecha(d.fecha_fin)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Button
                      size="sm"
                      color="link"
                      onClick={() => abrirEditar(d)}
                      style={{ padding: '2px 6px' }}
                      title="Editar"
                    >
                      <Edit3 size={14} />
                    </Button>
                    <Button
                      size="sm"
                      color="link"
                      onClick={() => pedirConfirmarBorrar(d)}
                      disabled={deletingId === d.id}
                      style={{ padding: '2px 6px', color: '#dc2626' }}
                      title="Eliminar"
                    >
                      {deletingId === d.id ? <Spinner size="sm" /> : <Trash2 size={14} />}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      <Modal isOpen={modalOpen} toggle={cerrarModal} centered>
        <ModalBody>
          <h5 style={{ marginBottom: '4px' }}>
            {editing ? `Editar descuento #${form.id}` : 'Nuevo descuento'}
          </h5>
          <p style={{ color: '#9ca3af', fontSize: '0.75rem', marginBottom: '16px' }}>
            Centro: <strong>{headquarter}</strong>
          </p>
          {error && (
            <p className="text-danger" style={{ fontSize: '0.8rem' }}>
              {error}
            </p>
          )}
          <Row className="mb-2">
            <Col md={12}>
              <Label><small>Descuento <span className="text-danger">*</span></small></Label>
              <Input
                type="text"
                name="descuento"
                value={form.descuento}
                onChange={handleChange}
                placeholder="Descripción del descuento"
                disabled={saving}
              />
            </Col>
          </Row>
          <Row className="mb-2">
            <Col md={4}>
              <Label><small>Porcentaje <span className="text-danger">*</span></small></Label>
              <Input
                type="number"
                name="porcentaje"
                value={form.porcentaje}
                onChange={handleChange}
                placeholder="0"
                min="0"
                max="100"
                step="0.01"
                disabled={saving}
              />
            </Col>
            <Col md={4}>
              <Label><small>Fecha inicio</small></Label>
              <Input
                type="date"
                name="fecha_inicio"
                value={form.fecha_inicio}
                onChange={handleChange}
                disabled={saving}
              />
            </Col>
            <Col md={4}>
              <Label><small>Fecha fin</small></Label>
              <Input
                type="date"
                name="fecha_fin"
                value={form.fecha_fin}
                onChange={handleChange}
                disabled={saving}
              />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={cerrarModal} disabled={saving}>
            <X size={14} /> Cancelar
          </Button>
          <Button color="primary" onClick={handleGuardar} disabled={saving}>
            {saving ? <Spinner size="sm" /> : <Save size={14} />} Guardar
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!confirmDelete} toggle={cancelarBorrar} centered>
        <ModalHeader toggle={cancelarBorrar}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <AlertTriangle size={18} color="#dc2626" /> Eliminar descuento
          </span>
        </ModalHeader>
        <ModalBody>
          <p style={{ marginBottom: '8px' }}>
            ¿Seguro que quieres eliminar este descuento? Esta acción no se puede deshacer.
          </p>
          {confirmDelete && (
            <div
              style={{
                fontSize: '0.85rem',
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '8px 12px',
              }}
            >
              <div><strong>ID:</strong> {confirmDelete.id}</div>
              <div><strong>Descuento:</strong> {confirmDelete.descuento}</div>
              <div><strong>Porcentaje:</strong> {confirmDelete.porcentaje}%</div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={cancelarBorrar} disabled={!!deletingId}>
            Cancelar
          </Button>
          <Button color="danger" onClick={confirmarBorrar} disabled={!!deletingId}>
            {deletingId ? <Spinner size="sm" /> : <Trash2 size={14} />} Eliminar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Descuentos;
